import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrashAlt,
    faBookOpen,
    faEllipsisV,
    faEye
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../../Services/ApiServices';
import { toastHelper } from '../../../Helpers/toastHelper';
import SoftGrid from '../../Shared/SoftGrid/SoftGrid';
import DeleteAlert from '../../Shared/DeleteAlert';
import { useLanguage } from '../../../contexts/LanguageContext';
import DynamicModal from '../../Shared/DynamicModal';
import FileViewer from '../../Shared/FileViewer';
import * as Yup from 'yup';
import RafiqChatBot from '../../Shared/RafiqChatBot';
import { faMagic, faPalette, faDownload, faFilePowerpoint } from '@fortawesome/free-solid-svg-icons';
import botImage from "../../../assets/images/bot.png";
import { formatDate } from "../../../Helpers/dateHelpers";
import { AppKeys } from '../../Shared/AppKeys';
import { getPublicUrlFromPhysicalPath } from '../../../Helpers/UrlHelpers';
import PowerPointViewer from '../../Shared/PowerPointViewer/PowerPointViewer';
import htmlHelper from '../../../Helpers/htmlHelper';


const MozakaraManager = () => {
  const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const navigate = useNavigate();

    // Delete Alert State
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const [mozakaraToDelete, setMozakaraToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Attachment Delete Alert State
    const [isAttachmentDeleteAlertOpen, setIsAttachmentDeleteAlertOpen] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);
    const [deletingAttachment, setDeletingAttachment] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentMozakara, setCurrentMozakara] = useState(null);

    // File Viewer State
    const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
    const [viewerFiles, setViewerFiles] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);

    // AI Presentation Generation State
    const [isAiPresentationOpen, setIsAiPresentationOpen] = useState(false);
    const [isFetchingOld, setIsFetchingOld] = useState(false);
    const [isPptxViewerOpen, setIsPptxViewerOpen] = useState(false);
    const [generatedPptxUrl, setGeneratedPptxUrl] = useState('');

    const handleAiOption = async (option) => {
        const val = option.value || option.text;

        if (val === 'start_new') {
            setIsAiPresentationOpen(false);
            navigate('/teacher/stepper-demo', { state: { useAIUI: true } });
            return null;
        }

        if (val === 'view_old') {
            try {
                const response = await ApiServices.get('UserFiles');
                const files = response?.data || response;
                
                if (Array.isArray(files) && files.length > 0) {
                    const nextOptions = files.map((fileObj, index) => {
                        let relativePath = '';
                        let displayName = t('mz_aiOldFile') ? t('mz_aiOldFile').replace('{count}', index + 1) : `ملف قديم ${index + 1}`;
                        
                        if (typeof fileObj === 'string') {
                            relativePath = fileObj;
                        } else if (fileObj && fileObj.file_path) {
                            relativePath = fileObj.file_path;
                            if (fileObj.file_name) displayName = fileObj.file_name;
                        }

                        const url = getPublicUrlFromPhysicalPath(relativePath, AppKeys._MEDIA_URL_PRODUCTION);
                        
                        return {
                            text: displayName,
                            value: `open_old_file_${url}`,
                            icon: faFilePowerpoint
                        };
                    });
                    
                    return {
                        message: `${t('mz_aiOldFilesList') || 'إليك قائمة بالملفات القديمة'}\n(${t('mz_aiOldFilesHint') || 'اختر ملفاً لعرضه'})`,
                        nextOptions
                    };
                } else {
                    return {
                        message: t('mz_aiNoOldFiles') || 'لا توجد ملفات قديمة حالياً',
                        nextOptions: [
                            { text: t('mz_aiCreateNew'), value: "start_new", icon: faMagic }
                        ]
                    };
                }
            } catch (error) {
                return {
                    message: t('mz_aiOldFilesFail') || 'حدث خطأ أثناء جلب الملفات القديمة',
                    nextOptions: [
                        { text: t('mz_aiCreateNew'), value: "start_new", icon: faMagic }
                    ]
                };
            }
        }

        if (typeof val === 'string' && val.startsWith('open_old_file_')) {
            const fileUrl = val.replace('open_old_file_', '');
            setGeneratedPptxUrl(fileUrl);
            setIsPptxViewerOpen(true);
            setIsAiPresentationOpen(false); // Close chatbot
            return null;
        }

        return null;
    };

    const handleViewAttachment = (idx) => {
        const formattedFiles = currentMozakara.attachments.map(file => ({
            ...file,
            file_name: file.file_name || file.path || file.file_path || file.url || file.file_url || file.name || file.original_name
        }));
        setViewerFiles(formattedFiles);
        setViewerIndex(idx);
        setIsFileViewerOpen(true);
    };

    const fetchMozakara = async (page = pageNumber, size = pageSize, search = searchQuery) => {
        setLoading(true);
        try {
            const params = {
                PageSize: size,
                PageNumber: page,
                IsActive: true,
                ...(search && {
                    Word: search,
                    SearchTerm: search,
                    Search: search,
                    Name: search,
                    Title: search
                })
            };

            const response = await ApiServices.get('Teacher/Mozakara/getAll', { params });

            if (response && response.data) {
                setData(response.data.items || []);
                setTotalRecords(response.data.total_count || 0);
            } else if (response && response.data) {
                setData(response.data.items || []);
                setTotalRecords(response.data.totalCount || response.data.total_count || 0);
            }
        } catch (error) {
            
            toastHelper.error(t('mozakaraLoadFailed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchMozakara(pageNumber, pageSize, searchQuery);
        }, searchQuery ? 400 : 0);

        return () => clearTimeout(timeoutId);
    }, [pageNumber, pageSize, searchQuery, refreshTrigger]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
    };

    const handleSearchChange = (searchTerm) => {
        setSearchQuery(searchTerm);
        setPageNumber(1);
    };

    const filteredData = useMemo(() => {
        if (!searchQuery || !searchQuery.trim()) return data;
        const q = searchQuery.toLowerCase().trim();
        return (data || []).filter(item => {
            const idStr = String(item.id || '');
            const title = (item.title || item.name || '').toLowerCase();
            const desc = htmlHelper.stripHtml(item.description || '').toLowerCase();
            const mat = (item.material || item.subject_name || item.material_name || '').toLowerCase();
            const grade = (item.user_class_name || item.grade_name || item.className || '').toLowerCase();
            return idStr.includes(q) || title.includes(q) || desc.includes(q) || mat.includes(q) || grade.includes(q);
        });
    }, [data, searchQuery]);

    const handleDelete = (item) => {
        setMozakaraToDelete(item);
        setIsDeleteAlertOpen(true);
    };

    const confirmDelete = async () => {
        if (!mozakaraToDelete) return;

        setDeleting(true);
        try {
            await ApiServices.delete(`Teacher/Mozakara/delete/${mozakaraToDelete.id}`);
            toastHelper.success(t('mozakaraDeleteSuccess'));
            setIsDeleteAlertOpen(false);
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            toastHelper.error(error?.message || t('mozakaraDeleteFailed'));
        } finally {
            setDeleting(false);
            setMozakaraToDelete(null);
        }
    };

    const handleEdit = async (item) => {
        setLoading(true);
        try {
            const response = await ApiServices.get(`Teacher/Mozakara/${item.id}`);
            const data = response?.data?.items?.[0] || response?.data || {};
            setCurrentMozakara({ ...item, ...data });
            setModalMode('edit');
            setIsModalOpen(true);
        } catch (error) {
            toastHelper.error(t('loadingDataFailed') || 'فشل تحميل البيانات');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setCurrentMozakara(null);
        setModalMode('add');
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setCurrentMozakara(null);
    };

    const handleModalSubmit = async (formData) => {
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach((item) => {
                    if (item !== null && item !== undefined && item !== '') fd.append(k, item);
                });
                return;
            }
            if (v !== null && v !== undefined && v !== '') fd.append(k, v);
        });

        if (modalMode === 'add') {
            await ApiServices.post('Teacher/Mozakara/create', fd, true);
        } else {
            await ApiServices.put(`Teacher/Mozakara/update/${currentMozakara?.id}`, fd, true);
        }
    };

    const handleModalSuccess = () => {
        toastHelper.success(modalMode === 'add' ? t('addEditMozakaraCreateSuccess') || 'تمت الإضافة بنجاح' : t('addEditMozakaraUpdateSuccess') || 'تم التحديث بنجاح');
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDeleteAttachment = (attachment) => {
        setAttachmentToDelete(attachment);
        setIsAttachmentDeleteAlertOpen(true);
    };

    const confirmDeleteAttachment = async () => {
        if (!attachmentToDelete) return;
        
        setDeletingAttachment(true);
        try {
            await ApiServices.delete(`Teacher/Mozakara/deleteAttachment/${attachmentToDelete.id}`);
            toastHelper.success(t('addEditMozakaraDeleteAttachmentSuccess') || 'تم حذف المرفق بنجاح');
            
            setCurrentMozakara(prev => ({
                ...prev,
                attachments: prev.attachments.filter(a => a.id !== attachmentToDelete.id)
            }));
            
            setRefreshTrigger(prev => prev + 1);
            setIsAttachmentDeleteAlertOpen(false);
        } catch (error) {
            toastHelper.error(error?.message || t('mozakaraDeleteFailed') || 'فشل الحذف');
        } finally {
            setDeletingAttachment(false);
            setAttachmentToDelete(null);
        }
    };

    const formConfig = [
        {
            name: 'Title',
            type: 'text',
            label: t('mozakaraTitle'),
            required: true,
            defaultValue: currentMozakara?.title || currentMozakara?.name || '',
        },
        {
            name: 'UserClassId',
            type: 'async-select',
            label: t('grade'),
            optionsUrl: 'Lookup/UserClasses/Teacher',
            required: true,
            defaultValue: currentMozakara?.user_class_id || '',
            valueLabel: currentMozakara?.user_class_name || currentMozakara?.grade_name || ''
        },
        {
            name: 'Description',
            type: 'styledText',
            label: t('addEditMozakaraDescriptionLabel') || 'وصف المذكرة',
            height: 200,
            defaultValue: currentMozakara?.description || '',
        },
        {
            name: 'UseWatermark',
            type: 'switch',
            label: 'إضافة علامة مائية للملفات المرفوعة',
            defaultValue: currentMozakara?.use_watermark || false,
        },
        {
            name: 'Attachments',
            type: 'file',
            label: t('attachments'),
            required: modalMode === 'add',
            accept: ".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx",
            uploadMultipleFile: true,
            MaxFileSize: 100,
            helperNote: modalMode === 'edit' ? t('mz_helperNoteEdit') : t('mz_helperNoteAdd'),
            validation: Yup.mixed()
                .test(
                    'is-pdf-when-watermark',
                    t('mz_pdfWatermarkErr'),
                    function (value) {
                        const { UseWatermark } = this.parent;
                        if (UseWatermark && value) {
                            const files = Array.isArray(value) ? value : (value instanceof FileList || value instanceof Array ? Array.from(value) : [value]);
                            for (let file of files) {
                                if (!file) continue;
                                const isPdf = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));
                                if (!isPdf) return false;
                            }
                        }
                        return true;
                    }
                )
                .test(
                    'max-file-size',
                    t('addEditMozakaraMaxSizeError') || 'File exceeds maximum size (100 MB)',
                    function (value) {
                        if (!value) return true;
                        const files = Array.isArray(value) ? value : (value instanceof FileList || value instanceof Array ? Array.from(value) : [value]);
                        const maxSize = 100 * 1024 * 1024; // 100 MB
                        for (let file of files) {
                            if (!file) continue;
                            if (file.size && file.size > maxSize) {
                                return false;
                            }
                        }
                        return true;
                    }
                ),
            renderExtra: () => {
                if (modalMode !== 'edit' || !currentMozakara?.attachments || currentMozakara.attachments.length === 0) return null;
                return (
                    <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '10px', color: '#1e293b' }}>
                            {t('addEditMozakaraCurrentAttachments')}:
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {currentMozakara.attachments.map((file, idx) => {
                                const fileUrl = file.url || file.file_url || file.path || file.file_path;
                                const fileName = file.name || file.original_name || file.title || `${t('mz_attachedFile')} ${idx + 1}`;
                                return (
                                    <div key={idx} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        padding: '8px 12px', 
                                        backgroundColor: '#ffffff', 
                                        border: '1px solid #cbd5e1', 
                                        borderRadius: '6px', 
                                        transition: 'all 0.2s'
                                    }}>
                                        <button 
                                            type="button"
                                            onClick={() => handleViewAttachment(idx)}
                                            style={{ 
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                color: '#3b82f6', 
                                                textDecoration: 'none',
                                                fontSize: '0.9rem',
                                                flex: 1,
                                                padding: 0
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                        >
                                            <FontAwesomeIcon icon={faEye} style={{ marginLeft: language === 'ar' ? '8px' : '0', marginRight: language === 'en' ? '8px' : '0' }} />
                                            {fileName}
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => handleDeleteAttachment(file)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#ef4444',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title={t('delete')}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }
        }
    ];

    const columns = [
        {
            key: 'id',
            label: t('id') || 'ID',
            render: (value, item) => <span>#{item.id || '-'}</span>
        },
        {
            key: 'title',
            label: t('mozakaraTitle'),
            render: (value, item) => (
                <div className="mozakara-title-cell">
                    <div className="mozakara-icon-container">
                        <FontAwesomeIcon icon={faBookOpen} />
                    </div>
                    <div className="mozakara-info">
                        <span className="mozakara-name">{item.name || item.title || t('untitled')}</span>
                        {item.description && (() => {
                            const cleanDesc = htmlHelper.stripHtml(item.description);
                            return cleanDesc ? <span className="mozakara-desc" title={cleanDesc}>{cleanDesc.substring(0, 30)}{cleanDesc.length > 30 ? '...' : ''}</span> : null;
                        })()}
                    </div>
                </div>
            )
        },
        {
            key: 'material',
            label: t('examMaterial'),
            render: (value, item) => <span>{item.material || item.subject_name || item.material_name || '-'}</span>
        },
        {
            key: 'user_class_name',
            label: t('grade'),
            render: (value, item) => <span className="grade-badge">{item.user_class_name || item.grade_name || item.className || '-'}</span>
        },
        {
            key: 'attachments_count',
            label: t('attachments'),
            render: (value, item) => (
                <span className={`price-badge ${item.attachments_count > 0 ? 'paid' : 'free'}`}>
                    {item.attachments_count > 0 ? t('filesCount').replace('{count}', item.attachments_count) : t('noFiles')}
                </span>
            )
        },
        {
            key: 'created_date',
            label: t('createdDate'),
            render: (value, item) => {
                return <span>{value ? formatDate(value): '-'}</span>;
            }
        }
    ];

    return (
        <motion.div
            className="mozakara-manager"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            <div className="manager-header">
                <div className="header-title">
                    <h2>{t('mozakaraManagement')}</h2>
                    <p>{t('mozakaraManagementDesc')}</p>
                </div>

                <div className="header-actions">
                    <button className="add-btn" onClick={handleAdd}>
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t('addMozakara')}</span>
                    </button>
                </div>
            </div>
            <div className="manager-content">
                <SoftGrid
                    data={filteredData}
                    columns={columns}
                    loading={loading}
                    totalRecords={searchQuery ? filteredData.length : totalRecords}
                    currentPage={pageNumber}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    searchPlaceholder={t('searchMozakara')}
                    onSearch={handleSearchChange}      
                    isColumnSelectable={true}
                    isColumnDraggable={true}
                    isColumnSortable={true}
                    emptyMessage={t('noMozakara')}
                    actions={[
                        (row) => {
                            return [
                                {
                                    tooltip: t('edit'),
                                    icon: <FontAwesomeIcon icon={faEdit} />,
                                    onClick: () => handleEdit(row)
                                },
                                {
                                    tooltip: t('delete'),
                                    icon: <FontAwesomeIcon icon={faTrashAlt} />,
                                    onClick: () => handleDelete(row),
                                    className: "delete"
                                }
                            ];
                        }
                    ]}
                />
            </div>

            <DeleteAlert
                isOpen={isDeleteAlertOpen}
                onClose={() => setIsDeleteAlertOpen(false)}
                onConfirm={confirmDelete}
                loading={deleting}
                title={t('mozakaraDeleteConfirmTitle')}
                message={t('mozakaraDeleteConfirmMessage')}
                itemName={mozakaraToDelete?.name || mozakaraToDelete?.title}
            />

            <DeleteAlert
                isOpen={isAttachmentDeleteAlertOpen}
                onClose={() => setIsAttachmentDeleteAlertOpen(false)}
                onConfirm={confirmDeleteAttachment}
                loading={deletingAttachment}
                title={t('addEditMozakaraDeleteAttachmentTitle') || 'حذف المرفق'}
                message={t('addEditMozakaraDeleteAttachmentMessage') || 'هل أنت متأكد من حذف هذا المرفق؟'}
                itemName={attachmentToDelete?.name || attachmentToDelete?.original_name || attachmentToDelete?.title}
            />

            <DynamicModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                title={modalMode === 'add' ? t('addMozakara') : t('addEditMozakaraTitleEdit')}
                submitButtonText={modalMode === 'add' ? t('add') : t('save')}
                formConfig={formConfig}
                onSubmit={handleModalSubmit}
                onError={(err) => toastHelper.error(err?.message || 'حدث خطأ')}
                onSuccess={handleModalSuccess}
            />

            <FileViewer
                files={viewerFiles}
                initialIndex={viewerIndex}
                isOpen={isFileViewerOpen}
                onClose={() => setIsFileViewerOpen(false)}
            />

            {/* ChatBot Trigger */}
            <motion.button
                className="rafiq-chatbot-trigger"
                style={{
                    left: language === 'en' ? 'auto' : '2rem',
                    right: language === 'en' ? '2rem' : 'auto'
                }}
                onClick={() => setIsAiPresentationOpen(true)}
                whileHover={{ scale: 1.15, rotate: [0, -10, 10, -10, 0] }}
                whileTap={{ scale: 0.95 }}
                animate={{
                    y: [0, -15, 0],
                }}
                transition={{
                    y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                }}
                title={t('mz_aiCreateBtnTitle')}
            >
                <div className="chatbot-ring"></div>
                <img src={botImage} alt="Rafiq" className="chatbot-trigger-icon" />
            </motion.button>

            <RafiqChatBot
                isOpen={isAiPresentationOpen}
                onClose={() => {
                    setIsAiPresentationOpen(false);
                }}
                isAIChat={false}
                enableChat={false}
                welcomeMessage={language === 'en' ? 'Welcome! What would you like to do today?' : "مرحباً بك! ماذا تريد أن تفعل اليوم؟"}
                subtitle={t('mz_aiBetaHint')}
                options={[
                    { text: t('mz_aiCreateNew'), value: "start_new", icon: faMagic },
                    { text: t('mz_aiViewOld') || "عرض الملفات القديمة", value: "view_old", icon: faBookOpen }
                ]}
                onOptionSelect={handleAiOption}
                externalLoading={isFetchingOld}
            />

            <PowerPointViewer
                isOpen={isPptxViewerOpen}
                onClose={() => setIsPptxViewerOpen(false)}
                fileUrl={generatedPptxUrl}
            />

            <style jsx>{`
                .mozakara-manager {
            padding: 24px;
            background: #fff;
            min-height: calc(100vh - 48px);
            width: 100%;
            overflow-x: hidden;
            border-radius: 30px;
                }



        .manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }



                .manager-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .header-title h2 {
                    margin: 0 0 8px 0;
                    font-size: 1.5rem;
                    color: #1E293B;
                    font-weight: 700;
                }

                .header-title p {
                    margin: 0;
                    color: #64748B;
                    font-size: 0.95rem;
                }

                .header-actions {
                    display: flex;
                    gap: 16px;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .add-btn {
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-family: inherit;
                    font-weight: 600;
                    font-size: 0.95rem;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.3);
                }

                .add-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.4);
                }

                .mozakara-title-cell {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .mozakara-icon-container {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: #EEF2FF;
                    color: #6366F1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.1rem;
                }

                .mozakara-info {
                    display: flex;
                    flex-direction: column;
                }

                .mozakara-name {
                    font-weight: 600;
                    color: #1E293B;
                }

                .mozakara-desc {
                    font-size: 0.8rem;
                    color: #64748B;
                }

                .grade-badge {
                    background: #F1F5F9;
                    color: #475569;
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }

                .price-badge {
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 700;
                }

                .price-badge.free {
                    background: #DCFCE7;
                    color: #166534;
                }

                .price-badge.paid {
                    background: #FEF3C7;
                    color: #92400E;
                }

                .grid-actions {
                    display: flex;
                    gap: 8px;
                }

                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .view-btn {
                    background: #F1F5F9;
                    color: #64748B;
                }

                .view-btn:hover {
                    background: #E2E8F0;
                    color: #475569;
                }

                .edit-btn {
                    background: #EEF2FF;
                    color: #6366F1;
                }

                .edit-btn:hover {
                    background: #6366F1;
                    color: white;
                }

                .delete-btn {
                    background: #FEF2F2;
                    color: #EF4444;
                }

                .delete-btn:hover {
                    background: #EF4444;
                    color: white;
                }

                /* ChatBot Trigger Button */
                .rafiq-chatbot-trigger {
                    position: fixed;
                    bottom: 2rem;
                    left: 2rem;
                    width: 80px;
                    height: 80px;
                    background: transparent;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .chatbot-trigger-icon {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    filter: drop-shadow(0 10px 15px rgba(0,0,0,0.2));
                }

                .chatbot-ring {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(124, 58, 237, 0.4) 0%, rgba(124, 58, 237, 0) 70%);
                    animation: pulse-glow-ring 2s infinite ease-in-out;
                    z-index: -1;
                }

                @keyframes pulse-glow-ring {
                    0% { transform: scale(0.9); opacity: 0.6; }
                    50% { transform: scale(1.6); opacity: 1; box-shadow: 0 0 25px rgba(124, 58, 237, 0.8); }
                    100% { transform: scale(0.9); opacity: 0.6; }
                }

                @media (max-width: 768px) {
                    .manager-header {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    
                    
                    .add-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .rafiq-chatbot-trigger {
                        bottom: 1rem;
                        right: 1rem;
                        left: auto;
                        width: 70px;
                        height: 70px;
                    }
                }
            `}</style>
        </motion.div >
    );
};

export default MozakaraManager;
